/**
 * Custom subscription chooser — simple and variable products.
 *
 * Responsibilities:
 * - Toggle subscription / one-time mode on radio change.
 * - Update the main price block with the selected scheme price
 * - Write the active scheme key into the hidden convert_to_sub input.
 * - Update the labels on add to cart process in subscription mode
 * - Show frequency options
 * - Add to cart the subscription validated on product page
 * - Enforce minimum quantity when subscription mode is active
 */
export default function ($) {

  function initSimpleChooser() {
    const $chooser = $('.gt-subscription-chooser');
    if (!$chooser.length) return;

    const schemes = $chooser.data('schemes') || [];
    const forceSubOnly = $chooser.data('force-subscription') === 'true';
    const subMinQty = parseInt($chooser.data('sub-min-qty')) || 1;

    const $form = $chooser.nextAll('form.cart').first();
    const $hiddenInput = $form.find('.js-gt-convert-to-sub');
    const $btn = $form.find('.single_add_to_cart_button');
    const $btnLabel = $btn.find('span');
    const $quantityLabel = $form.find('.js-gt-quantity-label');
    const $radios = $chooser.find('.js-gt-purchase-mode-radio');
    const $schemeSelect = $chooser.find('.js-gt-scheme-select');
    const $optionsPanel = $chooser.find('.js-gt-subscription-options');
    const $qty = $form.find('.js-product-quantity');
    const $lessBtn = $form.find('.js-less-quantity');
    const originalMin = parseFloat($qty.attr('min')) || 1;

    // True while the quantity is being set programmatically from a radio, so the
    // reverse "quantity → radio" sync does not fight the "radio → quantity" one.
    let syncingQty = false;

    // Configured bundle steps, ascending, read from the server-rendered radios
    function bundleSteps() {
      return $radios.filter('[value="bundle"]').map(function () {
        return { step: parseInt($(this).data('bundle-qty'), 10) || 0, el: this };
      }).get().filter(s => s.step > 0).sort((a, b) => a.step - b.step);
    }
    const $startDate = $optionsPanel.find('.js-gt-start-date');
    const $hiddenStartDate = $form.find('.js-gt-start-date-hidden');
    const todayStr = new Date().toISOString().split('T')[0];
    const maxStr = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    $startDate.attr({min: todayStr, max: maxStr});
    if (!$startDate.val()) $startDate.val(todayStr);
    $hiddenStartDate.val($startDate.val());
    $startDate.on('change', function () { $hiddenStartDate.val($startDate.val()); });

    // Detach .price-per-kilo once to capture clean initialPriceHtml, then restore.
    const $priceBlock = $('.js-product-price.simple-price');
    const $perKiloEl = $priceBlock.find('.price-per-kilo').detach();
    const initialPerKilo = $perKiloEl.html();
    const initialPrice = $priceBlock.html();
    if ($perKiloEl.length) $priceBlock.append($perKiloEl);

    function setPrice(priceHtml, perKiloHtml) {
      if (!$priceBlock.length) return;
      const $pk = $priceBlock.find('.price-per-kilo').detach();
      $priceBlock.html(priceHtml);
      if ($pk.length) {
        if (perKiloHtml !== undefined) $pk.html(perKiloHtml);
        $priceBlock.append($pk);
      }
    }

    function getScheme() {
      const key = $schemeSelect.length ? $schemeSelect.val() : null;
      return (key ? schemes.find(s => s.key === key) : null) || schemes[0] || null;
    }

    function updateLessBtn() {
      if (subMinQty <= 1) return;
      const current = parseFloat($qty.val()) || 1;
      const min = parseFloat($qty.attr('min')) || 1;
      $lessBtn.prop('disabled', current <= min);
    }

    function syncMode() {
      const mode = forceSubOnly ? 'subscription' : $radios.filter(':checked').val();

      if (mode === 'subscription') {
        $optionsPanel.removeClass('hidden');
        $quantityLabel.removeClass('hidden!').text($quantityLabel.data('sub-label') || '');
        $btnLabel.text($btn.data('subscribe-label') || '');
        const scheme = getScheme();
        $hiddenInput.val(scheme ? scheme.key : '0');
        if (scheme) setPrice(scheme.sub_price_html, scheme.sub_per_kilo_html || initialPerKilo);

        $qty.attr('min', subMinQty);
        if (parseFloat($qty.val()) < subMinQty) {
          $qty.val(subMinQty).trigger('change');
        }
        updateLessBtn();
        $startDate.prop('required', true);
        if (!$startDate.val()) $startDate.val(todayStr);
        $hiddenStartDate.val($startDate.val());
      } else {
        // One-time or "buy a bundle of X": both are one-time purchases. A bundle radio only pre-fills the quantity;
        // the quantity discount itself is applied by product-quantity-discount.js on the triggered change.
        $optionsPanel.addClass('hidden');
        $quantityLabel.addClass('hidden!');
        $btnLabel.text($btn.data('add-to-cart-label') || '');
        $hiddenInput.val('0');
        setPrice(initialPrice, initialPerKilo);

        const bundleQty = parseInt($radios.filter(':checked').data('bundle-qty'), 10);
        const targetQty = bundleQty > 0 ? bundleQty : 1;
        $qty.attr('min', originalMin);
        syncingQty = true;
        $qty.val(targetQty).trigger('change');
        syncingQty = false;
        $lessBtn.prop('disabled', false);
        $startDate.prop('required', false);
      }
    }

    // Reverse sync: when the quantity is edited by hand, reflect the matching purchase option (highest bundle step <= qty,
    // else "buy once"). Never runs in subscription mode (subscription owns the quantity), and never resets the quantity itself,
    // so the user keeps full manual control.
    function syncRadioFromQty() {
      if (forceSubOnly || syncingQty) return;
      if ($radios.filter(':checked').val() === 'subscription') return;

      const qty = parseInt($qty.val(), 10) || 1;
      let match = null;
      bundleSteps().forEach(s => { if (qty >= s.step) match = s; });

      const $target = match ? $(match.el) : $radios.filter('[value="one-time"]');
      if ($target.length && ! $target.prop('checked')) {
        $radios.prop('checked', false);
        $target.prop('checked', true);
      }
    }

    $qty.on('change input', updateLessBtn);
    $qty.on('change', syncRadioFromQty);

    if (forceSubOnly) syncMode();

    if (!forceSubOnly && new URLSearchParams(window.location.search).get('subscribe') === '1') {
      $radios.filter('[value="subscription"]').prop('checked', true);
      syncMode();
    }

    $radios.on('change', syncMode);
    $schemeSelect.on('change', function () {
      const scheme = getScheme();
      if (scheme) {
        $hiddenInput.val(scheme.key);
        setPrice(scheme.sub_price_html, scheme.sub_per_kilo_html || initialPerKilo);
      }
    });
  }

  function initVariableChooser() {
    const $form = $('.variations_form');
    if (!$form.length) return;

    const $chooser = $form.find('.js-gt-subscription-chooser-variable');
    const $hiddenInput = $form.find('.js-gt-convert-to-sub-var');
    const $btn = $form.find('.single_add_to_cart_button');
    const $btnLabel = $btn.find('span');
    const $qtyLabel = $form.find('.js-gt-quantity-label-var');
    const $radios = $chooser.find('.js-gt-purchase-mode-radio-var');
    const $schemeSelect = $chooser.find('.js-gt-scheme-select-var');
    const $singleLabel = $chooser.find('.js-gt-scheme-single-label');
    const $optionsPanel = $chooser.find('.js-gt-subscription-options-var');
    const $oneTimeRow = $chooser.find('.js-gt-one-time-row');
    const $badge = $chooser.find('.js-gt-discount-badge-var');
    const $bundleContainer = $chooser.find('.js-gt-bundle-options-var');
    const bundleLabelTpl = $bundleContainer.data('bundle-label') || 'Buy a bundle of %d';
    const $qty = $form.find('.js-product-quantity');
    const $lessBtn = $form.find('.js-less-quantity');
    const subMinQty = parseInt($chooser.data('sub-min-qty')) || 1;
    const originalMin = parseFloat($qty.attr('min')) || 1;
    const badgePrefix = $chooser.data('badge-prefix') || '';

    // True while the quantity is being set programmatically from a radio.
    let syncingQty = false;

    // Configured bundle steps, ascending, read from the current variation's radios.
    function bundleSteps() {
      return $chooser.find('.js-gt-purchase-mode-radio-var[value="bundle"]').map(function () {
        return { step: parseInt($(this).data('bundle-qty'), 10) || 0, el: this };
      }).get().filter(s => s.step > 0).sort((a, b) => a.step - b.step);
    }

    // Build "buy a bundle of X" radios (one per configured quantity-discount step).
    function buildBundleOptions(discounts) {
      $bundleContainer.empty();
      (discounts || []).forEach(function (d) {
        const step = parseInt(d.quantity_step, 10) || 0;
        const pct = parseInt(d.discount, 10) || 0;
        if (step <= 1 || pct <= 0) return;

        const $label = $('<label>', {'class': 'flex items-center gap-3 py-1 !mb-0 cursor-pointer'});
        const $input = $('<input>', {type: 'radio', name: 'gt_purchase_mode_var', value: 'bundle', 'class': 'js-gt-purchase-mode-radio-var'})
          .attr('data-bundle-qty', step);
        const $text = $('<span>', {'class': 'text-sm font-semibold', text: bundleLabelTpl.replace('%d', step)});
        const $pct = $('<span>', {
          'class': 'inline-flex items-center justify-center rounded-full border-2 border-primary bg-white text-primary text-xs font-bold shadow-sm px-2 py-1 leading-none whitespace-nowrap',
          text: '-' + pct + '%'
        });
        $label.append($input, ' ', $text, ' ', $pct);
        $bundleContainer.append($label);
      });
    }
    const $startDateVar = $optionsPanel.find('.js-gt-start-date');
    const todayStrVar = new Date().toISOString().split('T')[0];
    const maxStrVar = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    $startDateVar.attr({min: todayStrVar, max: maxStrVar});

    const $priceBlock = $('.js-product-price.variation-price');

    let schemes = [];
    let forceSubOnly = false;
    let oneTimePriceHtml = '';
    let oneTimePerKilo = '';

    function setPrice(priceHtml, perKiloHtml) {
      if (!$priceBlock.length) return;
      const $pk = $priceBlock.find('.price-per-kilo').detach();
      $priceBlock.html(priceHtml);
      if ($pk.length) {
        if (perKiloHtml !== undefined) $pk.html(perKiloHtml);
        $priceBlock.append($pk);
      }
    }

    function getScheme() {
      const key = $schemeSelect.is(':visible') ? $schemeSelect.val() : null;
      return (key ? schemes.find(s => s.key === key) : null) || schemes[0] || null;
    }

    function updateLessBtn() {
      if (subMinQty <= 1) return;
      const current = parseFloat($qty.val()) || 1;
      const min = parseFloat($qty.attr('min')) || 1;
      $lessBtn.prop('disabled', current <= min);
    }

    function syncMode() {
      const $checked = $chooser.find('.js-gt-purchase-mode-radio-var:checked');
      const mode = forceSubOnly ? 'subscription' : ($checked.val() || 'one-time');

      if (mode === 'subscription') {
        $optionsPanel.removeClass('hidden');
        $qtyLabel.removeClass('hidden!').text($qtyLabel.data('sub-label') || '');
        $btnLabel.text($btn.data('subscribe-label') || '');
        const scheme = getScheme();
        $hiddenInput.val(scheme ? scheme.key : '0');
        if (scheme) setPrice(scheme.sub_price_html, scheme.sub_per_kilo_html || oneTimePerKilo);

        $qty.attr('min', subMinQty);
        if (parseFloat($qty.val()) < subMinQty) {
          $qty.val(subMinQty).trigger('change');
        }
        updateLessBtn();
        $startDateVar.prop('required', true);
        if (!$startDateVar.val()) $startDateVar.val(todayStrVar);
      } else {
        // One-time or "buy a bundle of X": both one-time purchases. A bundle radio only pre-fills the quantity;
        // the quantity discount is applied by product-quantity-discount.js on the triggered change.
        $optionsPanel.addClass('hidden');
        $qtyLabel.addClass('hidden!');
        $btnLabel.text($btn.data('add-to-cart-label') || '');
        $hiddenInput.val('0');
        if (oneTimePriceHtml) setPrice(oneTimePriceHtml, oneTimePerKilo);

        const bundleQty = parseInt($checked.data('bundle-qty'), 10);
        const targetQty = bundleQty > 0 ? bundleQty : 1;
        $qty.attr('min', originalMin);
        syncingQty = true;
        $qty.val(targetQty).trigger('change');
        syncingQty = false;
        $lessBtn.prop('disabled', false);
        $startDateVar.prop('required', false);
      }
    }

    // Reverse sync: manual quantity edits reflect the matching purchase option
    // (highest bundle step <= qty, else "buy once"). Skipped in subscription
    // mode and while the chooser is hidden; never resets the quantity.
    function syncRadioFromQty() {
      if (forceSubOnly || syncingQty || $chooser.hasClass('hidden')) return;
      if ($chooser.find('.js-gt-purchase-mode-radio-var:checked').val() === 'subscription') return;

      const qty = parseInt($qty.val(), 10) || 1;
      let match = null;
      bundleSteps().forEach(s => { if (qty >= s.step) match = s; });

      const $target = match
        ? $(match.el)
        : $chooser.find('.js-gt-purchase-mode-radio-var[value="one-time"]');
      if ($target.length && ! $target.prop('checked')) {
        $chooser.find('.js-gt-purchase-mode-radio-var').prop('checked', false);
        $target.prop('checked', true);
      }
    }

    function populateChooser(variation) {
      schemes = variation.gt_subscription_schemes || [];
      forceSubOnly = variation.gt_force_subscription === true;
      oneTimePriceHtml = variation.gt_one_time_price_html || '';
      oneTimePerKilo = $priceBlock.find('.price-per-kilo').html() || '';

      if (!schemes.length) {
        $chooser.addClass('hidden');
        $hiddenInput.val('0');
        $startDateVar.prop('required', false);
        $qtyLabel.addClass('hidden!');
        $btnLabel.text($btn.data('add-to-cart-label') || '');
        $badge.addClass('hidden').text('');
        buildBundleOptions([]);
        $qty.attr('min', originalMin);
        $lessBtn.prop('disabled', false);
        return;
      }

      $chooser.removeClass('hidden');

      // "Buy a bundle of X" options are one-time purchases, so they are only offered when the subscription is not forced.
      buildBundleOptions(forceSubOnly ? [] : variation.gt_quantity_discounts);

      // One-time row visibility.
      const preselectSub = new URLSearchParams(window.location.search).get('subscribe') === '1';
      if (forceSubOnly) {
        $oneTimeRow.addClass('hidden');
        $radios.filter('[value="subscription"]').prop('checked', true);
        $radios.filter('[value="one-time"]').prop('checked', false);
      } else {
        $oneTimeRow.removeClass('hidden');
        if (preselectSub) {
          $radios.filter('[value="subscription"]').prop('checked', true);
          $radios.filter('[value="one-time"]').prop('checked', false);
        } else {
          $radios.filter('[value="one-time"]').prop('checked', true);
          $radios.filter('[value="subscription"]').prop('checked', false);
        }
      }

      // Check if all plans share the same discount (to suppress redundant labels).
      const nonZeroDiscounts = schemes.map(s => s.discount || 0).filter(d => d > 0);
      const allSameDiscount = nonZeroDiscounts.length > 0 && nonZeroDiscounts.every(d => d === nonZeroDiscounts[0]);

      // Badge: omit prefix when all plans share the same discount.
      const best = Math.max(0, ...schemes.map(s => s.discount || 0));
      if (best > 0) {
        const prefix = allSameDiscount ? '' : (badgePrefix ? badgePrefix + ' ' : '');
        $badge.text(prefix + '-' + best + '%').removeClass('hidden');
      } else {
        $badge.addClass('hidden').text('');
      }

      // Frequency options — omit per-option discount when all plans share the same discount.
      $schemeSelect.empty();
      schemes.forEach(function (s) {
        const label = s.freq_label + (!allSameDiscount && s.discount > 0 ? ' (-' + s.discount + '%)' : '');
        $schemeSelect.append($('<option>', {value: s.key, text: label}));
      });

      if (schemes.length === 1) {
        $schemeSelect.addClass('hidden');
        const s = schemes[0];
        $singleLabel.text(s.freq_label).removeClass('hidden');
      } else {
        $schemeSelect.removeClass('hidden');
        $singleLabel.addClass('hidden');
      }

      syncMode();
    }

    $qty.on('change input', updateLessBtn);
    $qty.on('change', syncRadioFromQty);

    $form.on('found_variation', function (e, variation) {
      populateChooser(variation);
    });

    $form.on('reset_data', function () {
      $chooser.addClass('hidden');
      $hiddenInput.val('0');
      $startDateVar.prop('required', false);
      $qtyLabel.addClass('hidden');
      $btnLabel.text($btn.data('add-to-cart-label') || '');
      $badge.addClass('hidden').text('');
      buildBundleOptions([]);
      schemes = [];
      $qty.attr('min', originalMin);
      $lessBtn.prop('disabled', false);
    });

    // Delegated: bundle radios are added dynamically per variation.
    $chooser.on('change', '.js-gt-purchase-mode-radio-var', syncMode);
    $schemeSelect.on('change', function () {
      const scheme = getScheme();
      if (scheme) {
        $hiddenInput.val(scheme.key);
        setPrice(scheme.sub_price_html, scheme.sub_per_kilo_html || oneTimePerKilo);
      }
    });
  }

  initSimpleChooser();
  initVariableChooser();
}
